import { Theme, useMediaQuery } from '@mui/material';
import { cloneElement, forwardRef, ReactElement, ReactNode } from 'react';
import Stack from '../Stack';
import LinkBox, { LinkBoxProps } from '../LinkBox';

export interface IconLinkBoxProps extends LinkBoxProps {
  /**
   * Icon on left side. Can be used owned image or icons from @o2/icons supported 48px size. On <MD devices the icon size is 32px.
   */
  startIcon: ReactElement;

  /**
   * If you need specific icon for small devices, you can override startIcon with passing new small icon in this prop. Icon size is 32px.
   */
  startIconSmall?: ReactElement;

  /**
   * Info icons on right side of LinkBox. Can show exponea or warning tooltips.
   */
  infoIcon?: ReactNode;
}

const IconLinkBox = forwardRef<HTMLButtonElement, IconLinkBoxProps>(
  ({ children, size = 'medium', infoIcon, startIcon, startIconSmall, ...other }, ref) => {
    const mdDown = useMediaQuery((theme: Theme) => theme.breakpoints.down('md'));
    const smallIcon = size === 'x-small' || size === 'small' || mdDown;

    const iconSmall = startIconSmall && cloneElement(startIconSmall, { width: 32 });
    const icon = startIcon && cloneElement(startIcon, { width: smallIcon ? 32 : 48 });

    return (
      <LinkBox ref={ref} size={size} {...other}>
        <Stack direction="row" gap={2} alignItems="center" sx={{ width: '100%' }}>
          {iconSmall && smallIcon ? startIconSmall : icon}
          <Stack flexGrow={1} direction="column" justifyContent="center">
            {children}
          </Stack>
          {infoIcon && (
            <Stack
              direction="row"
              justifyContent="end"
              gap={1.5}
              alignItems="center"
              sx={{ '& > .MuiSvgIcon-root': { fontSize: 32 } }}
            >
              {infoIcon}
            </Stack>
          )}
        </Stack>
      </LinkBox>
    );
  }
);

IconLinkBox.displayName = 'IconLinkBox';

export default IconLinkBox;
